import moment from "moment";
import { getShiftLength, parseBooleanVal } from "Utils/utils";

export const ADVANCE_FIELD_REQUIRED_MSG = "This field is required!";
export const ADVANCE_SHIFT_ZERO_DURATION_MSG =
  "Shift can't be zero hours & minutes!";
export const ADVANCE_SPLIT_SAME_TIME_MSG =
  "start time and end time can't be same!";

const ADVANCE_TIME_PARSE_FORMATS = ["HH:mm", "H:mm", "hh:mm A", "h:mm A"];

const parseAdvanceClockTime = (timeStr) => {
  if (timeStr == null || timeStr === "") return null;
  const trimmed = String(timeStr).trim();
  const parsed = moment(trimmed, ADVANCE_TIME_PARSE_FORMATS, true);
  if (parsed.isValid()) return parsed;
  const loose = moment(trimmed, ["HH:mm", "H:mm"], false);
  return loose.isValid() ? loose : null;
};

/** Same clock start/end = full 24-hour window in advance mode. */
export const isAdvanceSameClockTimeFullDay = (startTime, endTime) => {
  const startMoment = parseAdvanceClockTime(startTime);
  const endMoment = parseAdvanceClockTime(endTime);
  if (startMoment && endMoment) {
    return startMoment.format("HH:mm") === endMoment.format("HH:mm");
  }
  return (
    String(startTime ?? "").trim() !== "" &&
    String(startTime).trim() === String(endTime).trim()
  );
};

/** @deprecated Use isAdvanceSameClockTimeFullDay — kept for existing imports. */
export const isAdvanceMidnightToMidnightFullDay = isAdvanceSameClockTimeFullDay;

/** Advance mode: same clock time is a 24-hour window, not zero duration. */
export const isAdvanceShiftZeroDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return false;
  if (isAdvanceSameClockTimeFullDay(startTime, endTime)) return false;
  return getShiftLength(startTime, endTime, false, true) === false;
};

/** Top-level shift window — same rule as basic mode end_time validate. */
export const validateAdvanceShiftEndTime = (startTime, endTime) => {
  if (!startTime || !endTime) return true;
  if (isAdvanceShiftZeroDuration(startTime, endTime)) {
    return ADVANCE_SHIFT_ZERO_DURATION_MSG;
  }
  return true;
};

/** Participant/worker segment — same clock time = 24 hours in advance mode. */
export const validateAdvanceSplitEndTime = (startTime, endTime) => {
  if (!startTime || !endTime) return true;
  if (isAdvanceSameClockTimeFullDay(startTime, endTime)) return true;
  if (getShiftLength(startTime, endTime, false, true) === false) {
    return ADVANCE_SPLIT_SAME_TIME_MSG;
  }
  return true;
};

export const ADVANCE_SEGMENT_OUT_OF_WINDOW_MSG =
  "Time must be within the shift start and end time!";
export const ADVANCE_SEGMENT_START_OUT_OF_WINDOW_MSG =
  "Start time must be within the shift start and end time!";
export const ADVANCE_SEGMENT_END_AFTER_SHIFT_MSG =
  "End time can't be after the shift end time!";
export const ADVANCE_SEGMENT_END_BEFORE_START_MSG =
  "End time must be after start time!";
export const ADVANCE_SEGMENT_PARTICIPANT_OVERLAP_MSG =
  "This participant already has an overlapping time breakdown!";

/** Care-sector-aware overlap message (Participant → Consumer for aged care). */
export const getAdvanceParticipantOverlapMessage = (careSector) => {
  if (careSector === "aged_care") {
    return "This consumer already has an overlapping time breakdown!";
  }
  return ADVANCE_SEGMENT_PARTICIPANT_OVERLAP_MSG;
};
export const ADVANCE_SEGMENT_WORKER_OVERLAP_MSG =
  "This worker already has an overlapping time slot!";
export const ADVANCE_NO_WORKER_OPEN_SHIFT_MSG =
  "No worker selected. Turn on the Open shift toggle to create a shift without assigning a worker.";
export const ADVANCE_WORKER_START_MUST_MATCH_SHIFT_MSG =
  "At least one worker start time must match the shift start time.";
export const ADVANCE_WORKER_END_MUST_MATCH_SHIFT_MSG =
  "At least one worker end time must match the shift end time.";
export const ADVANCE_WORKER_SHIFT_WINDOW_COVERAGE_MSG =
  "At least one worker start time must match the shift start time, and at least one worker end time must match the shift end time.";

/** Advance create requires a worker unless Open shift is enabled. */
export const validateAdvanceWorkerOrOpenShift = (formValues = {}) => {
  if (parseBooleanVal(formValues?.advance_open_shift)) return true;

  const selectedWorkers = formValues?.advance_selected_workers || [];
  if (selectedWorkers.length > 0) return true;

  const carers = formValues?.advance_carers || [];
  const hasAssignedCarer = carers.some(
    (row) => row?.worker && !row?.is_open_slot
  );
  if (hasAssignedCarer) return true;

  const hasOpenSlot = carers.some((row) => row?.is_open_slot === true);
  if (hasOpenSlot) return true;

  return ADVANCE_NO_WORKER_OPEN_SHIFT_MSG;
};

const toAdvanceClockHmm = (value) => {
  const parsed = parseAdvanceClockTime(value);
  if (parsed) return parsed.format("HH:mm");
  return "";
};

const advanceClocksMatch = (left, right) => {
  const a = toAdvanceClockHmm(left);
  const b = toAdvanceClockHmm(right);
  return Boolean(a && b && a === b);
};

/**
 * Advance group (2+ worker/open-slot rows): at least one start must equal the
 * shift start, and at least one end must equal the shift end.
 * Open shift and single-worker rows are unchanged.
 */
export const validateAdvanceWorkerShiftWindowCoverage = (formValues = {}) => {
  if (parseBooleanVal(formValues?.advance_open_shift)) return true;

  const shiftStart = formValues?.start_time;
  const shiftEnd = formValues?.end_time;
  if (!shiftStart || !shiftEnd) return true;

  const carers = Array.isArray(formValues?.advance_carers)
    ? formValues.advance_carers
    : [];
  if (carers.length < 2) return true;

  const hasStartCoverage = carers.some((row) =>
    advanceClocksMatch(row?.worker_start_time, shiftStart)
  );
  const hasEndCoverage = carers.some((row) =>
    advanceClocksMatch(row?.worker_end_time, shiftEnd)
  );

  if (hasStartCoverage && hasEndCoverage) return true;
  if (!hasStartCoverage && !hasEndCoverage) {
    return ADVANCE_WORKER_SHIFT_WINDOW_COVERAGE_MSG;
  }
  if (!hasStartCoverage) return ADVANCE_WORKER_START_MUST_MATCH_SHIFT_MSG;
  return ADVANCE_WORKER_END_MUST_MATCH_SHIFT_MSG;
};

/** Minutes from shift start to the given clock time (wraps past midnight). */
const getAdvanceWindowOffsetMinutes = (shiftStart, time) => {
  const startMoment = parseAdvanceClockTime(shiftStart);
  const timeMoment = parseAdvanceClockTime(time);
  if (!startMoment || !timeMoment) return null;

  const startMinutes = startMoment.hour() * 60 + startMoment.minute();
  const timeMinutes = timeMoment.hour() * 60 + timeMoment.minute();
  let diff = timeMinutes - startMinutes;
  if (diff < 0) diff += 24 * 60;
  return diff;
};

/** Total shift window length in minutes; null when invalid/zero. */
export const getAdvanceShiftWindowMinutes = (shiftStart, shiftEnd) => {
  if (isAdvanceSameClockTimeFullDay(shiftStart, shiftEnd)) {
    return 24 * 60;
  }
  if (isAdvanceShiftZeroDuration(shiftStart, shiftEnd)) return null;
  const duration = getAdvanceWindowOffsetMinutes(shiftStart, shiftEnd);
  if (duration == null || duration === 0) return null;
  return duration;
};

/**
 * Duration of a participant/worker *segment* (not the top-level shift).
 *
 * Same-clock is 24h only when the parent shift is also a 24h same-clock window
 * (STA / midnight-to-midnight). Split leftovers seeded with start=end=shift end
 * on a shorter shift are 0 — not an extra 24h day.
 */
export const getAdvanceSegmentWindowMinutes = (
  segmentStart,
  segmentEnd,
  shiftStart,
  shiftEnd,
  { allowSameClockFullDay = true } = {}
) => {
  if (!segmentStart || !segmentEnd) return null;
  if (isAdvanceSameClockTimeFullDay(segmentStart, segmentEnd)) {
    if (!allowSameClockFullDay) return null;
    if (!isAdvanceSameClockTimeFullDay(shiftStart, shiftEnd)) return null;
    return 24 * 60;
  }
  return getAdvanceShiftWindowMinutes(segmentStart, segmentEnd);
};

/**
 * True for a split stub: same start/end that is not a real 24h shift window,
 * or a duplicate 24h copy after one full-day segment was already counted.
 */
export const isAdvanceUnallocatedSameClockSegment = (
  segmentStart,
  segmentEnd,
  shiftStart,
  shiftEnd,
  { alreadyCountedFullDay = false } = {}
) => {
  if (!segmentStart || !segmentEnd) return true;
  if (!isAdvanceSameClockTimeFullDay(segmentStart, segmentEnd)) return false;
  if (!isAdvanceSameClockTimeFullDay(shiftStart, shiftEnd)) return true;
  return Boolean(alreadyCountedFullDay);
};

/** Sum segment durations; at most one 24h same-clock window per list. */
export const sumAdvanceSegmentMinutes = (
  segments = [],
  shiftStart,
  shiftEnd
) => {
  let totalMinutes = 0;
  let countedFullDay = false;
  segments.forEach((segment) => {
    const start = segment?.start;
    const end = segment?.end;
    if (!start || !end) return;
    const isSameClock = isAdvanceSameClockTimeFullDay(start, end);
    const minutes = getAdvanceSegmentWindowMinutes(
      start,
      end,
      shiftStart,
      shiftEnd,
      { allowSameClockFullDay: !countedFullDay }
    );
    if (minutes == null || minutes <= 0) return;
    totalMinutes += minutes;
    if (isSameClock && minutes === 24 * 60) countedFullDay = true;
  });
  return totalMinutes;
};

/** Human-readable duration, e.g. 510 -> "8h 30m", 60 -> "1h", 45 -> "45m". */
export const formatAdvanceDuration = (minutes) => {
  if (minutes == null || minutes <= 0) return "";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours && mins) return `${hours}h ${mins}m`;
  if (hours) return `${hours}h`;
  return `${mins}m`;
};

/** Convenience: formatted length between two clock times, "" when invalid/zero. */
export const getAdvanceDurationLabel = (start, end) =>
  formatAdvanceDuration(getAdvanceShiftWindowMinutes(start, end));

/** Segment duration label — same-clock stubs on a non-24h shift are hidden. */
export const getAdvanceSegmentDurationLabel = (
  start,
  end,
  shiftStart,
  shiftEnd,
  { allowSameClockFullDay = true } = {}
) =>
  formatAdvanceDuration(
    getAdvanceSegmentWindowMinutes(start, end, shiftStart, shiftEnd, {
      allowSameClockFullDay,
    })
  );

/** Matches basic `getShiftLength` display, e.g. "24h:00m", "8h:30m". */
export const getAdvanceShiftLengthDisplay = (start, end) => {
  const minutes = getAdvanceShiftWindowMinutes(start, end);
  if (minutes == null || minutes <= 0) return null;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h:${String(mins).padStart(2, "0")}m`;
};

/** Segment start must fall inside the overall shift window. */
export const validateAdvanceSegmentStartTime = (shiftStart, shiftEnd, value) => {
  if (!value) return true;
  const duration = getAdvanceShiftWindowMinutes(shiftStart, shiftEnd);
  if (duration == null) return true;
  const offset = getAdvanceWindowOffsetMinutes(shiftStart, value);
  if (offset == null) return true;
  if (offset >= duration) return ADVANCE_SEGMENT_START_OUT_OF_WINDOW_MSG;
  return true;
};

/** Two [start,end] minute ranges overlap. Touching endpoints do NOT overlap. */
const advanceRangesOverlap = (aStart, aEnd, bStart, bEnd) =>
  aStart < bEnd && bStart < aEnd;

/**
 * Absolute minute range of a segment relative to the shift start, wrapping past
 * midnight so overnight segments compare correctly. Null when unparseable.
 */
const advanceSegmentMinutes = (shiftStart, start, end) => {
  const startOffset = getAdvanceWindowOffsetMinutes(shiftStart, start);
  let endOffset = getAdvanceWindowOffsetMinutes(shiftStart, end);
  if (startOffset == null || endOffset == null) return null;
  if (endOffset <= startOffset) endOffset += 24 * 60;
  return [startOffset, endOffset];
};

/**
 * Returns an overlap message when the segment at currentIndex overlaps another
 * segment that shares the same identity key (same participant / same worker).
 * Segments without a key or with missing times are skipped (no false errors).
 *
 * @param {string} shiftStart overall shift start clock time
 * @param {Array<{key: any, start: string, end: string}>} segments
 * @param {number} currentIndex
 * @param {string} overlapMsg
 */
export const validateAdvanceSegmentNoOverlap = (
  shiftStart,
  segments = [],
  currentIndex = -1,
  overlapMsg = ADVANCE_SEGMENT_PARTICIPANT_OVERLAP_MSG
) => {
  const current = segments?.[currentIndex];
  if (!current || current.key == null || current.key === "") return true;
  if (!current.start || !current.end) return true;

  const currentRange = advanceSegmentMinutes(shiftStart, current.start, current.end);
  if (!currentRange) return true;

  for (let i = 0; i < segments.length; i++) {
    if (i === currentIndex) continue;
    const other = segments[i];
    if (!other || other.key == null || other.key !== current.key) continue;
    if (!other.start || !other.end) continue;

    const otherRange = advanceSegmentMinutes(shiftStart, other.start, other.end);
    if (!otherRange) continue;

    if (
      advanceRangesOverlap(
        currentRange[0],
        currentRange[1],
        otherRange[0],
        otherRange[1]
      )
    ) {
      return overlapMsg;
    }
  }
  return true;
};

/** Segment end: not same as start, after start, and inside the shift window. */
export const validateAdvanceSegmentEndTime = (
  shiftStart,
  shiftEnd,
  segmentStart,
  value
) => {
  if (!value) return true;

  const duration = getAdvanceShiftWindowMinutes(shiftStart, shiftEnd);
  if (duration == null) return true;

  // Same clock start/end means a 24h segment — only valid inside a 24h shift window.
  if (isAdvanceSameClockTimeFullDay(segmentStart, value)) {
    if (duration < 24 * 60) return ADVANCE_SEGMENT_END_AFTER_SHIFT_MSG;
    return true;
  }

  const segStartMoment = parseAdvanceClockTime(segmentStart);
  const valueMoment = parseAdvanceClockTime(value);
  if (
    segStartMoment &&
    valueMoment &&
    segStartMoment.format("HH:mm") === valueMoment.format("HH:mm")
  ) {
    if (duration < 24 * 60) return ADVANCE_SEGMENT_END_AFTER_SHIFT_MSG;
    return true;
  }

  const endOffset = getAdvanceWindowOffsetMinutes(shiftStart, value);
  if (endOffset == null) return true;

  const startOffset = getAdvanceWindowOffsetMinutes(shiftStart, segmentStart);

  // Overnight segments (e.g. 08:00 PM → 12:00 AM on a 24h midnight→midnight shift):
  // end clock can be earlier than start. Match advanceSegmentMinutes so an end at the
  // shift-start clock resolves to the window end (duration), not offset 0.
  if (startOffset != null && endOffset <= startOffset) {
    const resolvedEndOffset = endOffset + 24 * 60;
    if (resolvedEndOffset > duration) {
      return ADVANCE_SEGMENT_END_BEFORE_START_MSG;
    }
    return true;
  }

  if (endOffset > duration) return ADVANCE_SEGMENT_END_AFTER_SHIFT_MSG;
  return true;
};

export const advanceParticipantValidationRules = {
  validate: (value) =>
    (Array.isArray(value) && value.length > 0) || ADVANCE_FIELD_REQUIRED_MSG,
};

/** Field paths to validate before advance-mode shift create. */
export const getAdvanceModeValidationFieldPaths = (formValues = {}) => {
  const paths = [
    "shift_date",
    "start_time",
    "end_time",
    "advance_selected_participants",
  ];

  (formValues.advance_splits || []).forEach((_, index) => {
    paths.push(
      `advance_splits.${index}.participant_start_time`,
      `advance_splits.${index}.participant_end_time`,
      `advance_splits.${index}.invoice_service_type_id`,
      `advance_splits.${index}.invoice_item_id`
    );
  });

  (formValues.advance_carers || []).forEach((row, index) => {
    paths.push(
      `advance_carers.${index}.worker_start_time`,
      `advance_carers.${index}.worker_end_time`
    );
  });

  return paths;
};

/** Participant/worker time fields that must stay inside the overall shift window. */
export const getAdvanceSegmentTimeFieldPaths = (formValues = {}) => {
  const paths = [];

  (formValues.advance_splits || []).forEach((_, index) => {
    paths.push(
      `advance_splits.${index}.participant_start_time`,
      `advance_splits.${index}.participant_end_time`
    );
  });

  (formValues.advance_carers || []).forEach((_, index) => {
    paths.push(
      `advance_carers.${index}.worker_start_time`,
      `advance_carers.${index}.worker_end_time`
    );
  });

  return paths;
};

export const getAdvanceFieldRequiredError = (fieldError) => {
  if (!fieldError) return null;
  if (fieldError.type === "required") return ADVANCE_FIELD_REQUIRED_MSG;
  return fieldError.message || ADVANCE_FIELD_REQUIRED_MSG;
};

export const getAdvanceSelectRequiredError = (fieldError) =>
  getAdvanceFieldRequiredError(fieldError);

/** Hide advance validation UI until user attempts Create / Create and Publish. */
export const getAdvanceValidationMessage = (showValidationErrors, fieldError) => {
  if (!showValidationErrors || !fieldError) return null;
  if (fieldError.message) return fieldError.message;
  return getAdvanceFieldRequiredError(fieldError);
};
